import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Reveal from "./Reveal";

const CARDS = [
  {
    id: "first-meeting",
    title: "The First Meeting — NOK 2,222",
    stanzas: [
      { t: "p", x: "We begin with a private meeting where we explore the story, the people, the materials and the intention behind what you would like to create." },
      { t: "p", x: "The NOK 2,222 is a consulting fee — for my time, presence and guidance in the meeting." },
      { t: "p", x: "There is no obligation to continue beyond this meeting." },
      { t: "p", x: "If we decide to create something together, the full amount of NOK 2,222 will be deducted from the final price of your commission." },
    ],
  },
  {
    id: "commissioned-pieces",
    title: "Commissioned Pieces — from NOK 4,444",
    stanzas: [
      { t: "p", x: "Commissioned pieces within a handheld size, created with up to six people, begin at NOK 4,444." },
      { t: "p", x: "Bigger pieces that require more time, materials and hands are priced on demand." },
      { t: "p", x: "Before any work begins, we will agree on the direction and price together." },
    ],
  },
  {
    id: "co-creation",
    title: "Co-Creation Gatherings",
    stanzas: [
      { t: "lead", x: "Some pieces are created not only for people, but with them." },
      { t: "p", x: "For families, friends, colleagues or other groups who wish to gather and take part in the making of a shared piece, I offer private co-creation sessions for groups of up to six people." },
      { t: "p", x: "The price is shaped by the piece we are creating, the materials involved and the length and nature of our time together." },
      { t: "p", x: "For gatherings of more than six people, I create a bespoke proposal. Larger groups require more facilitation and may include an additional assistant so that the process can remain intimate, present and properly held." },
    ],
  },
];

function Lines({ text }) {
  return text.split("\n").map((line, j) => (
    <span key={j}>
      {j > 0 && <br />}
      {line}
    </span>
  ));
}

export default function ProcessPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Process & Investment — The Things We Keep";
    return () => {
      document.title = "The Things We Keep — Objects with a story";
    };
  }, []);

  return (
    <div className="bg-paper pb-24 pt-32 sm:pb-32 sm:pt-40" data-testid="process-page">
      <div className="mx-auto max-w-3xl px-5 sm:px-8">
        <Reveal>
          <p className="ttwk-label" data-testid="process-eyebrow">
            For those considering a piece
          </p>
          <h1 className="mt-6 font-serif text-5xl font-light tracking-tight sm:text-6xl" data-testid="process-title">
            Process &amp; Investment
          </h1>
          <p className="mt-6 font-serif text-2xl font-light italic text-terracotta sm:text-3xl">
            Every piece begins with a conversation.
          </p>
        </Reveal>

        {CARDS.map((card, i) => (
          <Reveal key={card.id}>
            <article
              className="img-frame mt-12 rounded-sm border border-clay/60 bg-cream p-8 sm:p-12"
              data-testid={`process-card-${card.id}`}
            >
              <h2 className="font-serif text-2xl font-light leading-snug sm:text-3xl">{card.title}</h2>
              <div className="mt-6">
                {card.stanzas.map((s, j) =>
                  s.t === "lead" ? (
                    <p key={j} className="mb-6 font-serif text-xl italic leading-relaxed text-terracotta sm:text-2xl">
                      <Lines text={s.x} />
                    </p>
                  ) : (
                    <p key={j} className="mb-5 font-serif text-lg font-light leading-relaxed text-umber sm:text-xl">
                      <Lines text={s.x} />
                    </p>
                  )
                )}
              </div>
            </article>
          </Reveal>
        ))}

        <Reveal>
          <p className="mt-16 text-center font-serif text-2xl font-light italic leading-snug text-terracotta sm:text-3xl">
            Every piece is different.
            <br />
            Every story asks for something different.
          </p>
        </Reveal>
        <Reveal>
          <p className="mt-8 text-center text-base leading-relaxed text-umber/80">
            These prices are therefore starting points rather than a catalogue — a way of giving you
            an understanding of the investment before we begin.
          </p>
        </Reveal>

        <Reveal>
          <button
            onClick={() => navigate("/", { state: { scrollTo: "commission" } })}
            className="mt-14 rounded-full bg-terracotta px-10 py-4 text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-espresso"
            data-testid="process-cta"
          >
            Create together
          </button>
        </Reveal>
      </div>
    </div>
  );
}
